// components/LandingPage.jsx - 首页 (redesigned)
import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../theme';
import { useAuth } from '../AuthContext';
import { AuthModal } from './AuthModal';
import { SignalBadge } from './UIComponents';
import { logOut, updateQueryCount, checkQueryLimit, addSearchHistory, getSearchHistory, clearSearchHistory, getPortfolioHistory, clearPortfolioHistory, getWatchlist, removeFromWatchlist, recordLoginDay, getPickAllowance, getTodayPick, saveTodayPick, getTrialInfo } from '../firebase';

export function LandingPage({ onUpgrade, onPricing, onPortfolio, onTerms, onDisclaimer, onFeedback, onMarketSense}) {
  const { user, userData } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [quickData, setQuickData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryInfo, setQueryInfo] = useState({ remaining: 5, limit: 5 });
  const [authModal, setAuthModal] = useState({ open: false, mode: 'signin', onSuccess: null });
  const [searchHistory, setSearchHistory] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPortfolioHistoryModal, setShowPortfolioHistoryModal] = useState(false);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [marketIndices, setMarketIndices] = useState(null);
  const [todayPicks, setTodayPicks] = useState(null); // array of picks
  const [pickAllowance, setPickAllowance] = useState(1);
  const [recentPicks, setRecentPicks] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const menuRef = useRef(null);

  const DEFAULT_TICKER = 'RKLB';
  const API_BASE = 'https://insightstock-api-358482645476.us-west2.run.app';

  useEffect(() => {
    const handleClickOutside = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSummary = async (ticker) => {
    try { setSummaryLoading(true); const res = await fetch(API_BASE + '/api/stock/free/' + ticker + '/summary'); const data = await res.json(); if (data.summary) setQuickData(prev => prev ? { ...prev, summary: data.summary } : prev); }
    catch (err) { console.error('Failed to fetch summary:', err); }
    finally { setSummaryLoading(false); }
  };

  useEffect(() => {
    if (!quickData) {
      fetch(API_BASE + '/api/stock/free/' + DEFAULT_TICKER).then(res => res.json()).then(data => { if (data.stockPrice && data.stockPrice.latestClose !== null) { setQuickData(data); if (!data.summary) fetchSummary(DEFAULT_TICKER); } }).catch(() => {});
    }
    fetch(API_BASE + '/api/market/indices').then(res => res.json()).then(data => { if (data.success && data.data) setMarketIndices(data.data); }).catch(() => {});
    // Fetch recent settled picks for the results table
    fetch(API_BASE + '/api/recent-picks').then(res => res.json()).then(data => { if (data.success && data.picks) setRecentPicks(data.picks); }).catch(() => {});
  }, []);

  // Pick allocation: load saved picks or allocate new ones
  useEffect(() => {
    if (!user) { setTodayPicks(null); return; }

    const loadPicks = async () => {
      // Record login day for pick allocation tracking
      await recordLoginDay(user.uid);

      // Check if picks already saved for today
      const saved = await getTodayPick(user.uid);
      if (saved && saved.length > 0) {
        setTodayPicks(saved);
        setPickAllowance(saved.length);
        return;
      }

      // Determine allowance
      const isPro = userData?.isPro || false;
      const isProPlus = userData?.tier === 'proplus_monthly' || userData?.tier === 'proplus_yearly';
      const allowance = await getPickAllowance(user.uid, isPro, isProPlus);
      setPickAllowance(allowance);

      // Fetch from API and allocate picks
      try {
        const res = await fetch(API_BASE + '/api/top-picks');
        const data = await res.json();
        if (data.picks && data.picks.length > 0) {
          // Randomly select `allowance` picks from available
          const shuffled = [...data.picks].sort(() => Math.random() - 0.5);
          const selected = shuffled.slice(0, Math.min(allowance, shuffled.length));
          setTodayPicks(selected);
          // Save to Firebase (locked for today)
          await saveTodayPick(user.uid, selected);
        }
      } catch (err) {
        console.error('Failed to load top picks:', err);
      }
    };

    loadPicks();
  }, [user, userData]);

  useEffect(() => {
    if (user) { checkQueryLimit(user.uid).then(info => setQueryInfo(info)); getSearchHistory(user.uid).then(history => setSearchHistory(history)); getPortfolioHistory(user.uid).then(history => setPortfolioHistory(history)); getWatchlist(user.uid).then(list => setWatchlist(list)); }
    else { setSearchHistory([]); setPortfolioHistory([]); }
  }, [user, userData]);

  const handleQuickSearch = (e) => { e.preventDefault(); if (!searchInput.trim()) return; if (!user) { setError('Please sign in to search stocks.'); setAuthModal({ open: true, mode: 'signin' }); return; } searchTicker(searchInput.trim()); };

  const searchTicker = async (ticker) => {
    if (user) { const limitCheck = await checkQueryLimit(user.uid); if (!limitCheck.allowed) { setError(limitCheck.isPro ? 'Daily Pro limit (100) reached. Come back tomorrow!' : 'Daily free limit (5) reached. Upgrade to Pro for 100 queries/day!'); return; } }
    setSearchInput(ticker); setLoading(true); setError(null); setQuickData(null);
    fetch(API_BASE + '/api/stock/free/' + ticker).then(res => { if (res.status === 429) throw new Error('Daily query limit reached.'); return res.json(); })
      .then(async data => {
        if (!data.stockPrice || data.stockPrice.latestClose === null) { setError('Symbol "' + ticker + '" not found.'); setLoading(false); return; }
        setQuickData(data); setLoading(false);
        if (!data.summary) fetchSummary(ticker.toUpperCase());
        if (user) { await updateQueryCount(user.uid); const newInfo = await checkQueryLimit(user.uid); setQueryInfo(newInfo); const priceAtSearch = data.stockPrice?.latestClose || null; const newHistory = await addSearchHistory(user.uid, ticker, priceAtSearch); setSearchHistory(newHistory); }
      }).catch((err) => { setError(err.message || 'Failed to load data'); setLoading(false); });
  };

  const handleSignOut = async () => { setShowUserMenu(false); await logOut(); setQuickData(null); setSearchHistory([]); fetch(API_BASE + '/api/stock/free/' + DEFAULT_TICKER).then(res => res.json()).then(data => { if (data.stockPrice && data.stockPrice.latestClose !== null) setQuickData(data); }).catch(() => {}); };
  const handleDeleteAccount = async () => {
    if (!user) return;
    const confirmed = window.confirm('Are you sure you want to delete your account? This action cannot be undone. Your subscription will be cancelled and all data will be permanently deleted.');
    if (!confirmed) return;
    const doubleConfirm = window.confirm('This is permanent. Type OK to confirm you want to delete your account forever.');
    if (!doubleConfirm) return;
    try {
      setShowUserMenu(false);
      await fetch(API_BASE + '/api/delete-account', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: user.uid }) });
      await logOut();
      setQuickData(null);
      setSearchHistory([]);
      window.location.reload();
    } catch (err) { console.error('Delete account failed:', err); alert('Failed to delete account. Please contact support@insightstock.com'); }
  };
  const handleClearHistory = async () => { if (user) { await clearSearchHistory(user.uid); setSearchHistory([]); } };
  const handleClearPortfolioHistory = async () => { if (user) { await clearPortfolioHistory(user.uid); setPortfolioHistory([]); } };
  const scrollToSearch = () => { const el = document.getElementById('search-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); };
  const formatTime = (isoString) => { const date = new Date(isoString); const now = new Date(); const diffMs = now - date; const diffMins = Math.floor(diffMs / 60000); const diffHours = Math.floor(diffMs / 3600000); const diffDays = Math.floor(diffMs / 86400000); if (diffMins < 1) return 'Just now'; if (diffMins < 60) return diffMins + 'm ago'; if (diffHours < 24) return diffHours + 'h ago'; if (diffDays < 7) return diffDays + 'd ago'; return date.toLocaleDateString(); };
  const formatFullTime = (isoString) => new Date(isoString).toLocaleString();

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: theme.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

      <AuthModal isOpen={authModal.open} onClose={() => setAuthModal({ ...authModal, open: false })} onSuccess={authModal.onSuccess} mode={authModal.mode} setMode={(mode) => setAuthModal({ ...authModal, mode })} />

      {/* ===== History Modal ===== */}
      {showHistoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowHistoryModal(false)}>
          <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '500px', maxHeight: '70vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}><h3 style={{ color: theme.text, fontSize: '24px', margin: 0 }}>Search History</h3><button onClick={() => setShowHistoryModal(false)} style={{ background: 'transparent', border: 'none', color: theme.textDark, fontSize: '24px', cursor: 'pointer' }}>✕</button></div>
            {searchHistory.length === 0 ? <p style={{ color: theme.textDark, textAlign: 'center', padding: '40px 0' }}>No search history yet.</p> : (<><div style={{ marginBottom: '16px' }}>{searchHistory.map((item, index) => (<div key={index} onClick={() => { setShowHistoryModal(false); searchTicker(item.ticker); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: index < searchHistory.length - 1 ? '1px solid ' + theme.border : 'none', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(45, 212, 191, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><span style={{ color: theme.teal, fontSize: '18px', fontWeight: 600 }}>{item.ticker}</span><span style={{ color: theme.textDark, fontSize: '14px' }}>{formatFullTime(item.searchedAt)}</span></div>))}</div><button onClick={handleClearHistory} style={{ width: '100%', background: 'transparent', border: '1px solid ' + theme.red, borderRadius: '8px', padding: '12px', color: theme.red, fontSize: '14px', cursor: 'pointer' }}>Clear All History</button></>)}
          </div>
        </div>
      )}

      {/* ===== Portfolio History Modal ===== */}
      {showPortfolioHistoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowPortfolioHistoryModal(false)}>
          <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '500px', maxHeight: '70vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}><h3 style={{ color: theme.text, fontSize: '24px', margin: 0 }}>Portfolio History</h3><button onClick={() => setShowPortfolioHistoryModal(false)} style={{ background: 'transparent', border: 'none', color: theme.textDark, fontSize: '24px', cursor: 'pointer' }}>✕</button></div>
            {portfolioHistory.length === 0 ? <p style={{ color: theme.textDark, textAlign: 'center', padding: '40px 0' }}>No portfolio analysis yet.</p> : (<><div style={{ marginBottom: '16px' }}>{portfolioHistory.map((item, index) => (<div key={index} onClick={() => { setShowPortfolioHistoryModal(false); onPortfolio(item.data); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: index < portfolioHistory.length - 1 ? '1px solid ' + theme.border : 'none', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><div><span style={{ color: theme.purple, fontSize: '16px', fontWeight: 600 }}>{item.name || 'Portfolio'}</span><span style={{ color: theme.textDark, fontSize: '14px', marginLeft: '12px' }}>{item.tradesCount} trades</span></div><span style={{ color: theme.textDark, fontSize: '14px' }}>{formatTime(item.analyzedAt)}</span></div>))}</div><button onClick={handleClearPortfolioHistory} style={{ width: '100%', background: 'transparent', border: '1px solid ' + theme.red, borderRadius: '8px', padding: '12px', color: theme.red, fontSize: '14px', cursor: 'pointer' }}>Clear All History</button></>)}
          </div>
        </div>
      )}

      {/* ===== Watchlist Modal ===== */}
      {showWatchlistModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowWatchlistModal(false)}>
          <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}><h3 style={{ color: theme.text, fontSize: '24px', margin: 0 }}>Watchlist</h3><button onClick={() => setShowWatchlistModal(false)} style={{ background: 'transparent', border: 'none', color: theme.textDark, fontSize: '24px', cursor: 'pointer' }}>✕</button></div>
            {watchlist.length === 0 ? <p style={{ color: theme.textDark, textAlign: 'center', padding: '40px 0' }}>No stocks in watchlist. Add stocks from the analysis page.</p> : (
              <div style={{ marginBottom: '16px' }}>{watchlist.map((item, index) => {
                const price = typeof item.priceAtAdd === 'number' ? item.priceAtAdd : null;
                const risk = typeof item.riskLevel === 'string' ? item.riskLevel : null;
                const company = typeof item.companyName === 'string' && isNaN(Number(item.companyName)) ? item.companyName : '';
                return (
                <div key={index} style={{ padding: '16px', borderBottom: index < watchlist.length - 1 ? '1px solid ' + theme.border : 'none', cursor: 'pointer' }} onClick={() => { setShowWatchlistModal(false); onUpgrade(item.ticker); }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(250, 204, 21, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div><span style={{ color: '#facc15', fontSize: '20px', fontWeight: 600 }}>{item.ticker}</span><span style={{ color: theme.textMuted, fontSize: '16px', marginLeft: '12px' }}>{company}</span></div>
                    <button onClick={(e) => { e.stopPropagation(); if (user) { removeFromWatchlist(user.uid, item.ticker); setWatchlist(watchlist.filter(w => w.ticker !== item.ticker)); } }} style={{ background: 'transparent', border: 'none', color: theme.textDark, fontSize: '18px', cursor: 'pointer', padding: '4px 8px' }}>✕</button>
                  </div>
                  {price && <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 6px' }}>Price: <span style={{ color: theme.text, fontWeight: 500 }}>${price}</span></p>}
                  {risk && <p style={{ color: theme.textDark, fontSize: '14px', margin: 0 }}>Risk: <span style={{ color: risk.indexOf('Low') >= 0 ? theme.green : risk.indexOf('High') >= 0 ? theme.red : '#facc15', fontWeight: 500 }}>{risk}</span></p>}
                </div>
              );})}</div>
            )}
          </div>
        </div>
      )}

      {/* ===== NAV ===== */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 40px', borderBottom: '1px solid ' + theme.border,
        background: 'rgba(13, 17, 23, 0.95)', backdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '20px', fontWeight: 700, color: theme.text, letterSpacing: '-0.5px' }}>
            Insight<span style={{ color: theme.teal }}>Stock</span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              {[
                { label: 'Full Analysis', onClick: () => onUpgrade('AAPL') },
                { label: 'Market Sense', onClick: () => onMarketSense() },
                { label: 'Portfolio', onClick: () => setShowPortfolioHistoryModal(true) },
                { label: 'Feedback', onClick: () => onFeedback() },
              ].map((item, i) => (
                <span key={i} onClick={item.onClick} style={{
                  color: theme.textDark, fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'color 0.2s',
                }} onMouseEnter={e => e.currentTarget.style.color = theme.text}
                   onMouseLeave={e => e.currentTarget.style.color = theme.textDark}>
                  {item.label}
                </span>
              ))}
            </div>
          )}

          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: theme.textDark, fontSize: '13px' }}>{queryInfo.remaining}/{queryInfo.limit}</span>
              <div style={{ position: 'relative' }} ref={menuRef}>
                <button onClick={() => setShowUserMenu(!showUserMenu)} style={{
                  width: '36px', height: '36px', background: userData?.isPro ? theme.purple : theme.teal,
                  border: 'none', borderRadius: '50%', color: userData?.isPro ? '#fff' : theme.bg,
                  fontSize: '14px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{user.email?.charAt(0).toUpperCase() || 'U'}</button>
                {showUserMenu && (
                  <div style={{ position: 'absolute', top: '44px', right: 0, background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '12px', padding: '8px 0', minWidth: '200px', zIndex: 100, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid ' + theme.border }}>
                      <p style={{ color: theme.text, fontSize: '14px', fontWeight: 600, margin: '0 0 2px' }}>{user.email}</p>
                      <p style={{ color: userData?.isPro ? theme.purple : theme.teal, fontSize: '12px', margin: 0 }}>{userData?.isPro ? (userData?.isProPlus ? 'Pro+' : 'Pro') : 'Free Plan'}</p>
                    </div>
                    <button onClick={() => { setShowUserMenu(false); setShowHistoryModal(true); }} style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 16px', color: theme.text, fontSize: '14px', textAlign: 'left', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(45,212,191,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>Search History</button>
                    <button onClick={() => { setShowUserMenu(false); setShowWatchlistModal(true); }} style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 16px', color: theme.text, fontSize: '14px', textAlign: 'left', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(45,212,191,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>Watchlist {watchlist.length > 0 && `(${watchlist.length})`}</button>
                    <button onClick={() => { setShowUserMenu(false); onPricing(); }} style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 16px', color: theme.text, fontSize: '14px', textAlign: 'left', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(45,212,191,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>Upgrade</button>
                    <div style={{ borderTop: '1px solid ' + theme.border }}>
                      <button onClick={handleSignOut} style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 16px', color: theme.text, fontSize: '14px', textAlign: 'left', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(45,212,191,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>Sign Out</button>
                      <button onClick={handleDeleteAccount} style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 16px', color: theme.red, fontSize: '12px', textAlign: 'left', cursor: 'pointer', opacity: 0.7 }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,81,73,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>Delete Account</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button onClick={() => setAuthModal({ open: true, mode: 'signup' })} style={{
              background: theme.teal, color: theme.bg, border: 'none', padding: '8px 18px',
              borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}>Sign Up Free</button>
          )}
        </div>
      </nav>

      {/* ===== MARKET BAR ===== */}
      {marketIndices && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '24px', padding: '12px 40px',
          borderBottom: '1px solid ' + theme.border, background: theme.bgCard,
        }}>
          {['SPY', 'QQQ', 'DIA'].map(idx => {
            const data = marketIndices[idx];
            if (!data || !data.price) return null;
            const isUp = (data.dailyChange || 0) >= 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <span style={{ color: theme.textDark, fontWeight: 500 }}>{data.name}</span>
                <span style={{ color: theme.text, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>${data.price}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', fontWeight: 500, color: isUp ? theme.green : theme.red }}>
                  {isUp ? '+' : ''}{data.dailyChange}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== HERO ===== */}
      <section style={{ textAlign: 'center', padding: '80px 40px 50px' }}>
        <h1 style={{ fontSize: '56px', fontWeight: 800, letterSpacing: '-2px', lineHeight: 1.1, marginBottom: '20px' }}>
          AI-Powered <span style={{ color: theme.teal }}>Stock Analysis</span>
        </h1>
        <p style={{ color: theme.textDark, fontSize: '22px', margin: '0 auto 10px', lineHeight: 1.6, whiteSpace: 'nowrap' }}>
          Confused with numerous indicators and still don't know which to pick?
        </p>
        <p style={{ color: theme.text, fontSize: '24px', margin: '0 auto 16px', fontWeight: 700 }}>
          Our AI scans 12,000+ stocks daily.<br />See what it picks in 3 seconds.
        </p>
        {!user && (
          <div style={{ margin: '0 auto' }}>
            <p style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '8px' }}>
              <span style={{ color: theme.green }}>100% Free</span> <span style={{ color: theme.textDark }}>· No credit card needed</span>
            </p>
            <p style={{ color: theme.textDark, fontSize: '16px' }}>
              Get daily AI top picks, full analysis, and market summaries.<br />Sign up with email and start instantly.
            </p>
          </div>
        )}
      </section>

      {/* ===== AI PERFORMANCE + TODAY'S PICK ===== */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 60px' }}>

        {/* Stats Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', background: theme.green, borderRadius: '50%', display: 'inline-block', animation: 'pulse 2s ease-in-out infinite' }} />
            AI Performance
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0', marginBottom: '32px', borderRadius: '14px', overflow: 'hidden', border: '1px solid ' + theme.border }}>
          {[
            { value: '12,000+', label: 'Stocks Scanned Daily', color: theme.teal },
            { value: '50+', label: 'Filtered Candidates', color: theme.teal },
            { value: 'AI', label: 'News Risk Scoring', color: theme.green },
            { value: String(pickAllowance), label: pickAllowance >= 5 ? 'Top Picks / Day' : (pickAllowance === 1 ? 'Top Pick / Day' : 'Top Picks / Day'), color: theme.green },
          ].map((stat, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.02)', borderRight: i < 3 ? '1px solid ' + theme.border : 'none',
              padding: '28px 20px', textAlign: 'center', transition: 'all 0.3s', cursor: 'default',
            }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '30px', fontWeight: 700, color: stat.color, marginBottom: '6px' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '11px', color: theme.textDark, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 500 }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Today's Pick */}
        <div style={{
          background: theme.bgCard, border: '1px solid ' + theme.teal, borderRadius: '16px',
          padding: '28px', marginBottom: '24px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: theme.teal, marginBottom: '16px' }}>
            <span style={{ width: '6px', height: '6px', background: theme.teal, borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
            Today's AI Top Random Picks — {todayStr}
          </div>
          <div>
            {user && todayPicks && todayPicks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {todayPicks.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: theme.teal, fontWeight: 600, background: 'rgba(45,212,191,0.1)', padding: '3px 10px', borderRadius: '6px' }}>
                      {todayPicks.length} picks today
                    </span>
                    {pickAllowance <= 2 && (
                      <span style={{ fontSize: '11px', color: theme.textDark }}>
                        Log in daily to unlock more picks!
                      </span>
                    )}
                  </div>
                )}
                {todayPicks.map((pick, idx) => (
                  <div key={pick.ticker} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', padding: todayPicks.length > 1 ? '14px 16px' : '0', background: todayPicks.length > 1 ? theme.bg : 'transparent', borderRadius: '10px', border: todayPicks.length > 1 ? '1px solid ' + theme.border : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      {todayPicks.length > 1 && (
                        <span style={{ fontSize: '12px', color: theme.textDark, fontWeight: 600, minWidth: '20px' }}>#{idx + 1}</span>
                      )}
                      <div style={{
                        fontFamily: "'JetBrains Mono', monospace", fontSize: todayPicks.length > 1 ? '28px' : '42px', fontWeight: 700,
                        color: theme.text, letterSpacing: '2px',
                      }}>{pick.ticker}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '15px', color: theme.textDark, fontWeight: 500 }}>${pick.price}</span>
                        <span style={{ fontSize: '13px', color: theme.green, fontWeight: 500 }}>
                          Confidence: {pick.score >= 6 ? 'High' : pick.score >= 4 ? 'Medium' : 'Low'}
                        </span>
                        <span style={{ display: 'inline-block', background: pick.risk === 'low' ? 'rgba(34,197,94,0.15)' : 'rgba(224,170,62,0.15)', color: pick.risk === 'low' ? theme.green : '#e0aa3e', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', width: 'fit-content' }}>
                          {pick.risk === 'low' ? 'Low' : 'Medium'} Risk
                        </span>
                      </div>
                    </div>
                    <button onClick={() => onUpgrade(pick.ticker)} style={{
                      background: theme.teal, color: theme.bg, border: 'none', padding: todayPicks.length > 1 ? '10px 24px' : '14px 32px',
                      borderRadius: '10px', fontSize: todayPicks.length > 1 ? '13px' : '15px', fontWeight: 700, cursor: 'pointer',
                      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                    }}>
                      View Full Analysis →
                    </button>
                  </div>
                ))}
              </div>
            ) : !user ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: '42px', fontWeight: 700,
                    color: theme.text, filter: 'blur(10px)', userSelect: 'none', letterSpacing: '4px',
                  }}>XXXX</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '13px', color: theme.green, fontWeight: 500 }}>Confidence: High</span>
                    <span style={{ fontSize: '14px', color: theme.textDark }}>Direction: Bullish</span>
                    <span style={{ display: 'inline-block', background: 'rgba(34, 197, 94, 0.15)', color: theme.green, fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', width: 'fit-content' }}>Low Risk</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <button onClick={() => setAuthModal({ open: true, mode: 'signup' })} style={{
                    background: theme.teal, color: theme.bg, border: 'none', padding: '14px 32px',
                    borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                  }}>
                    Sign Up Free →
                  </button>
                  <div style={{ fontSize: '12px', color: theme.textDark, marginTop: '6px' }}>
                    No credit card needed
                  </div>
                </div>
              </div>
            ) : (
              <p style={{ color: theme.textDark, fontSize: '14px', margin: 0 }}>Loading today's picks...</p>
            )}
          </div>
        </div>

        {/* Recent AI Picks Results Table */}
        <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '16px', overflow: 'hidden', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid ' + theme.border }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>Recent AI <span style={{ color: '#EAB308', fontWeight: 800, fontStyle: 'italic', textDecoration: 'underline', textDecorationColor: '#EAB308' }}>TOP 1</span> Picks & Results</h3>
            <span style={{ fontSize: '12px', color: theme.textDark, background: theme.bg, padding: '4px 10px', borderRadius: '6px' }}>Rolling 7 trading days</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '650px' }}>
            <thead>
              <tr>
                {['Date', 'AI Pick', 'AI Call', 'Day 1', 'Day 3', '1 Week', 'Outcome'].map(h => (
                  <th key={h} style={{ textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: theme.textDark, padding: '12px 16px', borderBottom: '1px solid ' + theme.border }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentPicks.length > 0 ? recentPicks.map((row, i) => {
                const pctCell = (val) => (
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: !val ? theme.neutral : (val.startsWith('+') ? theme.green : theme.red) }}>
                    {val || <span style={{ fontSize: '11px', background: 'rgba(234,179,8,0.1)', padding: '2px 6px', borderRadius: '4px', color: theme.neutral }}>—</span>}
                  </td>
                );
                return (
                  <tr key={i} style={{ borderBottom: i < recentPicks.length - 1 ? '1px solid rgba(48, 54, 61, 0.5)' : 'none' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{row.date}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: theme.teal }}>{row.ticker}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{row.call} {row.call === 'Bullish' ? '▲' : row.call === 'Bearish' ? '▼' : '→'}</td>
                    {pctCell(row.day1)}
                    {pctCell(row.day3)}
                    {pctCell(row.day7)}
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      {row.correct === null ? '—' : row.correct
                        ? <span style={{ fontSize: '12px', color: theme.green, background: 'rgba(34,197,94,0.1)', padding: '3px 8px', borderRadius: '4px', fontWeight: 500 }}>✓ Correct</span>
                        : <span style={{ fontSize: '12px', color: theme.red, background: 'rgba(239,68,68,0.1)', padding: '3px 8px', borderRadius: '4px', fontWeight: 500 }}>✗ Wrong</span>
                      }
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} style={{ padding: '24px', textAlign: 'center', fontSize: '14px', color: theme.textDark }}>
                    No settled picks yet — results appear after market close
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </section>

      {/* ===== SEARCH SECTION ===== */}
      <section id="search-section" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 60px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>Analyze Any Stock</h2>
        <form onSubmit={handleQuickSearch} style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <input
            type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
            placeholder="Enter ticker symbol... (e.g. AAPL, NVDA, TSLA)"
            style={{
              flex: 1, background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '10px',
              padding: '14px 18px', fontSize: '15px', color: theme.text, fontFamily: "'DM Sans', sans-serif",
              outline: 'none',
            }}
          />
          <button type="submit" disabled={loading} style={{
            background: theme.teal, color: theme.bg, border: 'none', padding: '14px 28px',
            borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}>{loading ? '...' : 'Quick Look'}</button>
        </form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
          {['TSLA', 'NVDA', 'MSFT', 'GOOGL', 'META', 'AAPL', 'PLTR', 'RKLB'].map(ticker => (
            <span key={ticker} onClick={() => user ? searchTicker(ticker) : setAuthModal({ open: true, mode: 'signin' })} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: theme.bgCard, border: '1px solid ' + theme.border, padding: '8px 14px',
              borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              fontFamily: "'JetBrains Mono', monospace", color: theme.text,
              transition: 'all 0.2s',
            }} onMouseEnter={e => { e.currentTarget.style.borderColor = theme.teal; }} onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}>
              {ticker}
            </span>
          ))}
        </div>

        {error && (<div style={{ background: 'rgba(248, 81, 73, 0.1)', border: '1px solid ' + theme.red, borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', textAlign: 'center' }}><p style={{ color: theme.red, fontSize: '14px', margin: 0 }}>{error}</p></div>)}

        {loading && !quickData && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '24px' }}>
            <div style={{ width: '20px', height: '20px', border: '2px solid ' + theme.border, borderTop: '2px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: theme.textDark, fontSize: '14px', margin: 0 }}>Fetching data for <span style={{ color: theme.teal, fontWeight: 600 }}>{searchInput}</span>...</p>
          </div>
        )}

        {quickData && quickData.stockPrice && (
          <div style={{ padding: '24px 0', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div><h3 style={{ fontSize: '24px', fontWeight: 700, color: theme.text, margin: '0 0 4px' }}>{quickData.company?.name || quickData.ticker}</h3><p style={{ color: theme.textDark, margin: 0, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>{quickData.ticker} · {quickData.company?.industry || 'N/A'}</p></div>
              <div style={{ textAlign: 'right' }}><p style={{ fontSize: '32px', fontWeight: 700, color: theme.text, margin: 0 }}>${quickData.stockPrice.latestClose}</p><p style={{ fontSize: '16px', fontWeight: 600, color: (quickData.stockPrice.dailyChange || 0) >= 0 ? theme.green : theme.red, margin: '4px 0 0' }}>{(quickData.stockPrice.dailyChange || 0) >= 0 ? '+' : ''}{quickData.stockPrice.dailyChange || 0}% (1D)</p></div>
            </div>
            {quickData.technical && (
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ color: theme.textDark, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Signal:</span>
                <SignalBadge signal={quickData.technical.overall} />
              </div>
            )}
            {(quickData.summary || summaryLoading) && (
              <div style={{ marginBottom: '16px' }}>
                {quickData.summary ? (
                  <p style={{ color: theme.textMuted, fontSize: '15px', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>"{quickData.summary}"</p>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '16px', height: '16px', border: '2px solid ' + theme.border, borderTop: '2px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    <p style={{ color: theme.textDark, fontSize: '14px', margin: 0, fontStyle: 'italic' }}>AI analyzing...</p>
                  </div>
                )}
              </div>
            )}
            <button onClick={() => { if (!user) { setAuthModal({ open: true, mode: 'signup' }); return; } onUpgrade(quickData.ticker); }} style={{
              background: theme.teal, color: theme.bg, border: 'none', padding: '12px 28px',
              borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}>View Full AI Analysis →</button>
          </div>
        )}
      </section>

      {/* ===== CHALLENGE SECTION ===== */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 70px' }}>
        <div style={{
          background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '20px',
          padding: '40px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '42px', marginBottom: '16px' }}>🧠 vs 🤖</div>
          <h2 style={{ fontSize: '30px', fontWeight: 800, marginBottom: '12px', letterSpacing: '-0.5px' }}>
            Think You Can <span style={{ color: theme.teal }}>Beat the AI</span>?
          </h2>
          <p style={{ color: theme.textDark, fontSize: '15px', maxWidth: '460px', margin: '0 auto 24px' }}>
            Make your daily stock call, compete against our AI, build your Market Sense IQ score and climb the leaderboard.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginBottom: '28px', flexWrap: 'wrap' }}>
            {[
              { num: '1', text: 'Pick a stock &\nmake your call' },
              { num: '2', text: 'Compare with\nAI\'s prediction' },
              { num: '3', text: 'Check results &\nearn IQ points' },
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  width: '32px', height: '32px', background: theme.teal, color: theme.bg,
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', fontWeight: 700, flexShrink: 0,
                }}>{step.num}</span>
                <span style={{ fontSize: '13px', color: theme.textDark, textAlign: 'left', whiteSpace: 'pre-line' }}>{step.text}</span>
              </div>
            ))}
          </div>
          <button onClick={() => user ? onMarketSense() : setAuthModal({ open: true, mode: 'signup' })} style={{
            background: 'transparent', color: theme.teal, border: '2px solid ' + theme.teal,
            padding: '14px 36px', borderRadius: '10px', fontSize: '15px', fontWeight: 700,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.3s',
          }} onMouseEnter={e => { e.currentTarget.style.background = theme.teal; e.currentTarget.style.color = theme.bg; }}
             onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.teal; }}>
            Start the Challenge →
          </button>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 70px' }}>
        <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 800, marginBottom: '10px', letterSpacing: '-0.5px' }}>Simple, Transparent Pricing</h2>
        <p style={{ textAlign: 'center', color: theme.textDark, fontSize: '16px', marginBottom: '40px' }}>Start free. Upgrade when you're ready.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { tier: 'Free', price: '$0', desc: 'Full access — forever', features: ['Real-time stock price', 'Technical signals', 'AI quick summary', 'Daily AI Top Pick', 'Full AI Analysis'], featured: false, badge: 'No credit card needed' },
            { tier: 'Pro', price: '$7.99', period: '/mo', desc: 'Deep analysis & insights', features: ['Everything in Free', 'Deep AI news analysis', 'Financial charts', 'Risk assessment', '5 AI picks daily'], featured: true, badge: 'Launch Price — 50% off' },
            { tier: 'Pro+', price: '$14.99', period: '/mo', desc: 'Full power for serious traders', features: ['Everything in Pro', 'Portfolio review', 'Upload CSV/PDF', 'Trade timing analysis', 'Personalized tips'], featured: false, badge: 'Launch Price — 50% off' },
          ].map((plan, i) => (
            <div key={i} onClick={plan.tier !== 'Free' ? onPricing : undefined} style={{
              background: theme.bgCard, border: '1px solid ' + (plan.featured ? theme.teal : theme.border),
              borderRadius: '14px', padding: '28px 24px', cursor: plan.tier !== 'Free' ? 'pointer' : 'default',
              position: 'relative', transition: 'all 0.3s',
            }}>
              {plan.featured && <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: theme.teal, color: theme.bg, fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '20px' }}>Most Popular</div>}
              <div style={{ color: theme.teal, fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{plan.tier}</div>
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>{plan.price}{plan.period && <span style={{ fontSize: '14px', color: theme.textDark, fontWeight: 400 }}>{plan.period}</span>}</div>
              {plan.badge && <div style={{ color: theme.green, fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>{plan.badge}</div>}
              <div style={{ color: theme.textDark, fontSize: '13px', marginBottom: '20px' }}>{plan.desc}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {plan.features.map((f, j) => (
                  <div key={j} style={{ fontSize: '13px', color: theme.textDark, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: theme.teal, fontWeight: 700, fontSize: '12px' }}>✓</span> {f}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SIGN UP CTA (for non-logged-in users) ===== */}
      {!user && (
        <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 70px', textAlign: 'center' }}>
          <div style={{ background: theme.bgCard, border: '1px solid ' + theme.teal, borderRadius: '16px', padding: '40px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 800, marginBottom: '14px', letterSpacing: '-0.5px' }}>
              Ready to see today's <span style={{ color: theme.teal }}>AI Top Pick</span>?
            </h2>
            <p style={{ color: theme.textDark, fontSize: '16px', marginBottom: '6px' }}>
              Sign up with email — unlock full analysis and daily stock recommendations.
            </p>
            <p style={{ color: theme.green, fontSize: '18px', fontWeight: 700, marginBottom: '24px' }}>
              100% Free · No credit card needed
            </p>
            <button onClick={() => setAuthModal({ open: true, mode: 'signup' })} style={{
              background: theme.teal, color: theme.bg, border: 'none', padding: '16px 48px',
              borderRadius: '10px', fontSize: '18px', fontWeight: 700, cursor: 'pointer',
            }}>
              Sign Up Free →
            </button>
          </div>
        </section>
      )}

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 70px' }}>
        <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 800, marginBottom: '10px', letterSpacing: '-0.5px' }}>
          Frequently Asked Questions
        </h2>
        <p style={{ textAlign: 'center', color: theme.textDark, fontSize: '16px', marginBottom: '40px' }}>
          Everything you need to know about InsightStock
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            {
              q: 'What is InsightStock?',
              a: 'InsightStock is an AI-powered stock analysis platform. Our AI scans 12,000+ US stocks every day, analyzing price action, technical signals, and news sentiment to surface the most promising opportunities — so you don\'t have to.'
            },
            {
              q: 'How does the AI pick stocks?',
              a: 'Each day our system pulls real-time market data from Polygon.io, runs technical analysis (moving averages, RSI, MACD, volume trends, etc.), scores news sentiment, and ranks every stock. The top-scoring tickers become the daily "AI Top Picks."'
            },
            {
              q: 'Is this financial advice?',
              a: 'No. InsightStock is an informational tool — it highlights data-driven signals and AI-generated insights, but it is not a licensed financial advisor. Always do your own research and consult a professional before making investment decisions.'
            },
            {
              q: 'What is "Market Sense"?',
              a: 'Market Sense is a fun, competitive feature where you pick a stock and compare your prediction against the AI\'s pick. It\'s a great way to test your market intuition and see how you stack up against our AI model.'
            },
            {
              q: 'Is it really free?',
              a: 'Yes! Sign up with your email and get full access to real-time prices, technical signals, AI summaries, daily AI Top Picks, full analysis, and Market Sense — completely free, no credit card needed. For advanced features like Portfolio Review, you can upgrade to Pro ($7.99/mo) or Pro+ ($14.99/mo).'
            },
            {
              q: 'Where does the data come from?',
              a: 'All market data is sourced from Polygon.io, a trusted financial data provider. AI analysis and summaries are generated by our proprietary models. Data refreshes daily on trading days.'
            },
          ].map((item, i) => (
            <div key={i} style={{
              background: theme.bgCard, border: '1px solid ' + (openFaq === i ? theme.teal : theme.border),
              borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.3s',
            }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{
                width: '100%', background: 'none', border: 'none', padding: '20px 24px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
              }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>{item.q}</span>
                <span style={{
                  color: theme.teal, fontSize: '20px', fontWeight: 700, transition: 'transform 0.3s',
                  transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)', flexShrink: 0, marginLeft: '16px',
                }}>+</span>
              </button>
              {openFaq === i && (
                <div style={{ padding: '0 24px 20px', color: theme.textDark, fontSize: '14px', lineHeight: '1.7' }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ textAlign: 'center', padding: '30px 40px', borderTop: '1px solid ' + theme.border, color: theme.textDark, fontSize: '13px' }}>
        <p style={{ margin: '0 0 8px' }}>Data provided by Polygon.io · Analysis by AI · © 2026 InsightStock</p>
        <p style={{ margin: '0 0 8px' }}>
          <span onClick={onTerms} style={{ color: theme.textMuted, textDecoration: 'underline', cursor: 'pointer' }}>Terms</span>
          {' · '}
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=a96b1561-8753-416f-8cf2-735b51664bb9" target="_blank" rel="noopener noreferrer" style={{ color: theme.textMuted, textDecoration: 'underline' }}>Privacy</a>
          {' · '}
          <span onClick={onDisclaimer} style={{ color: theme.textMuted, textDecoration: 'underline', cursor: 'pointer' }}>Disclaimer</span>
          {' · '}
          <span style={{ color: theme.textMuted }}>support@insightstock.io</span>
        </p>
        <p style={{ margin: 0, fontSize: '12px', opacity: 0.6 }}>For informational purposes only. Not financial advice. AI-generated content may contain errors.</p>
      </footer>
    </div>
  );
}

export default LandingPage;
